import { htmlToElement } from "@modules/bookmarks/html-parser"
import { createCaptchaWidget } from "@modules/captcha/captchaWidget.ts"
import { addEventListenerAll } from "@modules/util/events"
import { closeGenericModal, openGenericModal } from "@modules/util/generic-modal.ts"
import type { WidgetInstance } from "friendly-challenge"

export type PhoneNumberApiResponse = {
    phoneNumber: string
    phoneNumberNormalized?: string
    messageId: string
}

let phoneNumberButtonElements: NodeListOf<HTMLButtonElement> = [] as unknown as NodeListOf<HTMLButtonElement>
let phoneNumberLinkElements: NodeListOf<HTMLLinkElement> = [] as unknown as NodeListOf<HTMLLinkElement>
let clickedPhoneNumberLink: HTMLAnchorElement | null = null

const phoneNumberDisplayMobile = (phoneNumber: string): HTMLParagraphElement =>
    htmlToElement<HTMLParagraphElement>(
        `<a itemprop="telephone-link" href="tel:${phoneNumber}" class="btn btn-lg btn-block btn-ladies-primary m-5-b">
        <span class="icon light inline lower before-text icon-20 p-5-r"><svg><use href="#icon--single-advert--phone-receiver"></use></svg></span> <span itemprop="telephone">${phoneNumber}</span>
    </a>`,
    )

const phoneNumberDisplayPc = (phoneNumber: string): HTMLParagraphElement =>
    htmlToElement<HTMLParagraphElement>(`<p class="important"><strong itemprop="telephone">${phoneNumber}</strong></p>`)

const phoneNumberButtons = (): NodeListOf<HTMLButtonElement> => {
    if (!phoneNumberButtonElements.length) {
        phoneNumberButtonElements = document.querySelectorAll<HTMLButtonElement>(".reveal-phone-number-button")
    }

    return phoneNumberButtonElements
}

const phoneNumberLinks = (): NodeListOf<HTMLLinkElement> => {
    if (!phoneNumberLinkElements.length) {
        phoneNumberLinkElements = document.querySelectorAll<HTMLLinkElement>(
            ".send-whatsapp-link, .send-sms-link, .send-telegram-link",
        )
    }

    return phoneNumberLinkElements
}

const getPhoneNumberRevealListener =
    (nonce: string, captchaSolution = "") =>
    async (): Promise<void> => {
        const buttonsToReplace = phoneNumberButtons()
        const linksToReplace = phoneNumberLinks()
        const response: Response = await fetch(
            `/api/adverts/${SiteInfo.advertId}/phone-number?nonce=${nonce}&captchaSolution=${encodeURIComponent(captchaSolution)}`,
        )
        const data = (await response.json()) as PhoneNumberApiResponse

        if (data.messageId === "PHONE_NUMBER_ERROR_RATE_LIMIT") {
            const phoneNumberChallengeModal = openGenericModal(
                "Zu viele Anfragen in kurzer Zeit. Bitte verifiziere dich über die <b>Anti-Robot-Prüfung</b>, um die Sperre sofort aufzuheben und weitere Kontaktdaten abzurufen. Ansonsten versuche es bitte später erneut. <form>" +
                    "<div id='phone-number-captcha-container' class='friendly-captcha'></div>" +
                    "</form>",
                "<div>\n" +
                    '        <div class="col-xs-2 icon-modal-close pull-right generic-dialog-close-button-in-corner" title="Schließen">\n' +
                    '          <span class="icon dark block icon-24">\n' +
                    '            <svg class="icon" data-action="close" viewBox="0 0 20 20">\n' +
                    '              <path d="M0.726 0.028l10.003 9.999-10.002 9.946-0.726-0.73 9.27-9.217-9.271-9.267 0.726-0.73zM20 0.757l-9.27 9.268 9.27 9.218-0.726 0.73-10.003-9.946 10.003-10 0.726 0.73z"></path>\n' +
                    "            </svg>\n" +
                    "          </span>\n" +
                    "        </div>\n" +
                    '        <h3 id="add-bookmark-tag-login-label" class="modal-title">Limit erreicht</h3>\n' +
                    "      </div>",
                "phone-number-challenge-modal",
            )
            const closeButtonIcon = phoneNumberChallengeModal.querySelector(".generic-dialog-close-button-in-corner")
            if (closeButtonIcon) {
                closeButtonIcon.addEventListener("click", () => {
                    closeGenericModal(phoneNumberChallengeModal)
                    clickedPhoneNumberLink = null
                })
            }

            const closeButton = phoneNumberChallengeModal.querySelector("#generic-dialog-close-button")
            if (closeButton) {
                closeButton.addEventListener("click", () => {
                    clickedPhoneNumberLink = null
                })
            }

            const phoneNumberCaptchaContainer = phoneNumberChallengeModal.querySelector(
                "#phone-number-captcha-container",
            ) as HTMLElement

            const phoneNumberCaptchaWidget = createCaptchaWidget(
                phoneNumberCaptchaContainer,
                async (): Promise<void> => {
                    const captchaSolutionHtml = phoneNumberChallengeModal.querySelector(
                        "input.frc-captcha-solution",
                    ) as HTMLInputElement

                    const captchaSolutionValue = (captchaSolutionHtml.value || "").trim()

                    phoneNumberChallengeModal.remove()

                    await getPhoneNumberRevealListener(nonce, captchaSolutionValue)()

                    return
                },
                {
                    solutionFieldName: "phone-number-captcha-solution",
                    language: "de",
                },
            ) as WidgetInstance
            phoneNumberCaptchaWidget.init()

            return
        }

        if (typeof data.phoneNumber === "undefined") {
            openGenericModal("Es ist ein technisches Problem aufgetreten. Bitte versuche es nochmal.", "Fehler")
            clickedPhoneNumberLink = null
            return
        }

        let displayElement: HTMLElement
        if (SiteInfo.isMobileVersion) {
            displayElement = phoneNumberDisplayMobile(data.phoneNumber)
        } else {
            displayElement = phoneNumberDisplayPc(data.phoneNumber)
        }

        for (const button of buttonsToReplace) {
            button.replaceWith(displayElement.cloneNode(true))
        }

        if (typeof data.phoneNumberNormalized !== "undefined") {
            for (const link of linksToReplace) {
                const href = link.href
                link.href = href.replace("**********", data.phoneNumberNormalized)
            }
        }

        const phoneRevealedEvent = new CustomEvent("phoneNumberRevealed", {
            detail: {
                phoneNumber: data.phoneNumber,
                phoneNumberNormalized: data.phoneNumberNormalized ?? "",
            },
        })
        document.dispatchEvent(phoneRevealedEvent)

        return
    }

export const registerPhoneNumberRevealListener = (nonce: string): void => {
    addEventListenerAll(phoneNumberButtons(), "click", getPhoneNumberRevealListener(nonce), {
        once: false,
        passive: true,
    })

    addEventListenerAll(phoneNumberLinks(), "click", async (e) => {
        clickedPhoneNumberLink = e.currentTarget as HTMLAnchorElement

        const url = clickedPhoneNumberLink.href

        if (url.includes("**********")) {
            e.preventDefault()
            await getPhoneNumberRevealListener(nonce)()
        }
    })

    document.addEventListener("phoneNumberRevealed", () => {
        if (clickedPhoneNumberLink) {
            clickedPhoneNumberLink.click()
        }
    })

    if (SiteInfo.phoneNumberVisited) {
        if (phoneNumberButtonElements.length > 0) {
            const event = new Event("click")
            phoneNumberButtonElements[0].dispatchEvent(event)
        }
    }
}
